import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Layers, Loader2, Lock } from "lucide-react";
import { FEATURE_LABELS, FEATURE_PARENT } from "@/lib/features";

const PARENT_LABEL = { os: "Dentro da OS", checklist: "Checklist técnico", clientes: "Clientes", estoque: "Estoque", financeiro: "Financeiro", crm: "CRM", patio: "Pátio", relatorios: "Relatórios", fiscal: "Fiscal" };

export default function OwnerFeatures() {
  const { user, refresh } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/features/catalog").then(({ data }) => setCatalog(data.features)).catch((e) => toast.error(formatApiError(e))); }, []);
  const features = user?.shop?.features || {};
  const planBlocked = new Set(features._plan_blocked || []);
  const groups = {};
  for (const f of catalog) { const p = FEATURE_PARENT[f.key] || f.parent; (groups[p] ??= []).push(f); }
  const toggle = async (key, visible) => {
    setBusy(true);
    try {
      const next = { ...prefs, [key]: visible };
      setPrefs(next);
      await api.put("/features/prefs", { prefs: { [key]: visible } });
      refresh?.();
    } catch (e) { toast.error(formatApiError(e)); setPrefs(prefs); } finally { setBusy(false); }
  };
  return (
    <div data-testid="owner-features" className="space-y-5 animate-fade-up">
      <div>
        <div className="text-[11px] font-mono-caps text-zinc-500">Configurações</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100">Telas e funções</h1>
        <p className="text-sm text-zinc-400 mt-1">Ligue o que a sua oficina usa. O que estiver bloqueado está em outro plano.</p>
      </div>
      {Object.entries(groups).map(([parent, items]) => (
        <section key={parent} data-testid={`features-group-${parent}`} className="rounded-2xl border border-card2-border bg-card2 p-4 space-y-2">
          <div className="flex items-center gap-2 text-[11px] font-mono-caps text-zinc-500"><Layers className="h-4 w-4 text-green-neon" /> {PARENT_LABEL[parent] || parent}</div>
          {items.map((f) => {
            const blocked = planBlocked.has(f.key);
            const checked = features[f.key] !== false && !blocked;
            return (
              <div key={f.key} data-testid={`feature-row-${f.key}`} className={`flex items-center justify-between gap-2 py-1 ${blocked ? "opacity-70" : ""}`}>
                <span className="text-sm text-zinc-200 flex items-center gap-2">{blocked && <Lock className="h-3.5 w-3.5 text-amber-300" />}{FEATURE_LABELS[f.key] || f.label}</span>
                {blocked ? <Link data-testid={`feature-${f.key}-plans`} to="/owner/meu-plano" className="text-[11px] font-bold text-amber-300 underline">Ver planos</Link>
                  : <Switch data-testid={`feature-${f.key}-toggle`} disabled={busy} checked={checked} onCheckedChange={(v) => toggle(f.key, v)} />}
              </div>
            );
          })}
        </section>
      ))}
      <Button data-testid="features-reload" variant="outline" onClick={() => refresh?.()} disabled={busy} className="border-zinc-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Atualizar"}</Button>
    </div>
  );
}
